import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class NewAuthService {

  private refreshInProgress: Promise<string> | null = null;

  constructor(private msalService: MsalService, private readonly toastr: ToastrService, private readonly router: Router, private readonly dialog: MatDialog) {}

  // Call this to obtain a valid access token for the given scopes.
  // This will try a silent renewal first, forcing a refresh if needed.
  // Deduplicates concurrent refresh calls so only one runs at a time.
  async getAccessToken(scopes: string[]): Promise<string> {
    // If a refresh is already in progress, wait for it instead of starting another
    if (this.refreshInProgress) {
      return this.refreshInProgress;
    }

    this.refreshInProgress = this._acquireToken(scopes);

    try {
      const token = await this.refreshInProgress;
      return token;
    } finally {
      this.refreshInProgress = null;
    }
  }

  private async _acquireToken(scopes: string[]): Promise<string> {
    const accounts = this.msalService.instance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      throw new Error('No signed-in account found.');
    }

    const account = accounts[0];

    const silentRequest: SilentRequest = {
      scopes,
      account
    };

    try {
      // Let MSAL handle caching; it will use the refresh token automatically when the access token is expired
      const response = await this.msalService.instance.acquireTokenSilent(silentRequest);
      const newToken = response.idToken;

      // Persist to localStorage so subsequent requests use the fresh token, even in a new webview instance
      localStorage.setItem('bearer_token', newToken);

      return newToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        this.handleSessionExpiry();
        throw new Error('Interaction required to renew token.');
      }
      throw e;
    }
  }

  /** Clear session and redirect the user to the home/login page */
  handleSessionExpiry(): void {
    // Prevent showing session expired if already on login page or no session existed
    if (!localStorage.getItem('bearer_token') && !sessionStorage.getItem('userId')) {
      return;
    }
    sessionStorage.clear();
    localStorage.clear();
    this.dialog.closeAll();
    this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      disableClose: true,
      data: { message: 'Your session has expired. You will be redirected to log in again.', okOnly: true, title: 'Session Expired' }
    }).afterClosed().subscribe(() => {
      this.router.navigate(['/app-home'], { skipLocationChange: true });
    });
  }
}
